'use client';

import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { type ReactNode } from 'react';

import { Image, ImageProps, Note } from '@/app/components/ui';
import { page } from '@/app/styles/ui';
import { type Partner } from '@/configs/partner';

import NotFound from '../../ui/EmailForm/NotFound';
import EmailContent from './EmailContent';

export interface EmailLayoutProps {
  readonly title: ReactNode;
  readonly description: ReactNode;
  readonly help?: ReactNode;
  readonly imageAlt?: ImageProps['alt'];
  readonly imageSrc: ImageProps['src'];
  readonly note?: ReactNode;
  readonly partner: Partner;
  readonly isInvalid: boolean;
  readonly isMaintenance: boolean;
}

const EmailLayout = ({
  title,
  description,
  help,
  imageAlt = '',
  imageSrc,
  note,
  partner,
  isInvalid,
  isMaintenance,
}: EmailLayoutProps): JSX.Element => {
  return (
    <LayoutGrid className={page({ pt: 'standard' })} rowGap={40}>
      <GridItem
        rowStart={{ sm: 2, md: 1 }}
        rowEnd={{ sm: 3 }}
        colStart={{ sm: 'main', md: 15, lg: 16 }}
        colEnd={{ sm: 'main', xl: 23 }}
        className="flex flex-col gap-4"
      >
        <Typography Element="h4" variant="h4">
          {title}
        </Typography>

        <Typography variant="body-large">{description}</Typography>

        {note ? <Note>{note}</Note> : null}

        {!isMaintenance &&
          (isInvalid ? <NotFound /> : <EmailContent partner={partner} />)}

        {help && <div className="mt-8">{help}</div>}
      </GridItem>

      <GridItem
        rowStart={{ sm: 1 }}
        rowEnd={{ sm: 2 }}
        colStart={{ sm: 'main', md: 'main', xl: 3 }}
        colEnd={{ sm: 10, md: 14 }}
        className="hidden sm:flex md:block"
      >
        <div>
          <Image
            alt={imageAlt}
            role="presentation"
            src={imageSrc}
            width="600"
            height="600"
            priority
            className="w-full sm:rounded-md md:rounded-xl"
          />
        </div>
      </GridItem>
    </LayoutGrid>
  );
};

export default EmailLayout;
